/**
 * The standing-order editor's model (M6 plan P29, spec §6.1): pure state, rows, validation and the command it
 * sends. The editor (`OrderEditor.tsx`) renders an `EditorState` and dispatches `EditorAction`s; the ship detail
 * host (`shipDetailHost.tsx`) carries out the `EditorEffect`s.
 *
 * - **Data, not copies.** The limits, the order defaults, the allowed classes and the presets are orders.json's
 *   (parsed by the worker's own `ordersConfigSchema`), role defaults are roles.json's (`ROLE_PARAM_FIELDS`), and the
 *   command payload is checked with the worker's `standingOrderSchema`. Rows, steps and labels are
 *   `orderEditor.json`.
 * - **Validation mirrors `resolveStandingOrder`** (strategic/fleet/compile.ts): the class may take the order,
 *   params apply to the order's role, the date stop needs a day, `myStation` needs a station, supply and ferry need a
 *   target, `guardLane` a relay, escort a ship (never itself). Out-of-limits values are refused here (the worker
 *   clamps them), so what the player saves is what runs.
 * - **Presets** fill the draft the way the worker's `applyPreset` does (presets.ts `presetSpec`, keeping the ship's
 *   references and area). Saved unchanged, the editor sends `applyPreset` (the preset stays named on the ship);
 *   after any edit it sends `setOrder` (a custom order).
 * - **The command** (`commandOrder`) lists only the fields the order shows, params that differ from the defaults,
 *   stops and wares in the worker's order: the editor ↔ worker round trip is exact.
 *
 * Navigation (keyboard and pad): up/down move between rows; left/right change the value (step a number, cycle a
 * choice, move the option cursor of a list); activate toggles a stop or a list option, applies the preset or
 * presses an action; back closes.
 */
import { z } from 'zod'
import ordersJson from '../../data/strategic/orders.json'
import rolesJson from '../../data/strategic/roles.json'
import { ROLE_PARAM_FIELDS, isRoleParam, ordersConfigSchema, standingOrderSchema, type OrderRole, type OrdersConfig } from '../../strategic/schemas'
import {
  ORDER_NAMES,
  ORDER_PARAM_NAMES,
  PATROL_RULE_NAMES,
  ROE_NAMES,
  SELL_POLICY_NAMES,
  STOP_NAMES,
  type OrderName,
  type OrderParamName,
  type PatrolRuleName,
  type RoeName,
  type SellPolicyName,
  type ShipClassName,
  type StandingOrder,
  type StopName,
} from '../../strategic/types'
import { WARE_IDS, type WareId } from '../../strategic/wares'
import type { FleetCommand } from '../../fleet-link/service'
import type { NavInput } from '../strategic/mount'
import editorJson from './orderEditor.json'

// ---------------------------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------------------------

export const MULTI_FIELDS = ['wares', 'blockWares', 'systems', 'priority'] as const
export type MultiField = (typeof MULTI_FIELDS)[number]
export const REF_FIELDS = ['sellStation', 'target', 'laneRelay', 'escort'] as const
export type RefField = (typeof REF_FIELDS)[number]
export const ENUM_FIELDS = ['sellPolicy', 'patrolRule'] as const
export type EnumField = (typeof ENUM_FIELDS)[number]
export type RefKey = MultiField | RefField | EnumField
const REF_KEYS: readonly string[] = [...MULTI_FIELDS, ...REF_FIELDS, ...ENUM_FIELDS]

/** An editor field of an order: a numeric ORDER_PARAM, or a reference / list / choice field. */
export type FieldKey = OrderParamName | RefKey

const label = z.string().min(1)
const editorConfigSchema = z.strictObject({
  comment: z.string(),
  logLimit: z.number().int().min(0),
  fields: z.record(z.enum(ORDER_NAMES), z.array(z.string())),
  steps: z.partialRecord(z.enum(ORDER_PARAM_NAMES), z.number().positive()),
  labels: z.strictObject({
    params: z.partialRecord(z.enum(ORDER_PARAM_NAMES), label),
    refs: z.record(z.enum([...MULTI_FIELDS, ...REF_FIELDS, ...ENUM_FIELDS]), label),
    roe: z.record(z.enum(ROE_NAMES), label),
    stops: z.record(z.enum(STOP_NAMES), label),
    sellPolicy: z.record(z.enum(SELL_POLICY_NAMES), label),
    patrolRule: z.record(z.enum(PATROL_RULE_NAMES), label),
  }),
})
export type OrderEditorConfig = Omit<z.infer<typeof editorConfigSchema>, 'fields'> & { fields: Record<OrderName, readonly FieldKey[]> }

/** Everything the editor reads from data. */
export interface OrderEditorData {
  readonly orders: OrdersConfig
  /** roles.json's role-row values by ORDER_PARAM name. */
  readonly roleDefaults: Readonly<Record<OrderRole, Partial<Record<OrderParamName, number>>>>
  readonly config: OrderEditorConfig
}

const rolesShape = z.object({
  trader: z.record(z.string(), z.unknown()),
  miner: z.record(z.string(), z.unknown()),
  supplier: z.record(z.string(), z.unknown()),
  patrol: z.record(z.string(), z.unknown()),
})

function isParam(k: string): k is OrderParamName {
  return (ORDER_PARAM_NAMES as readonly string[]).includes(k)
}

/** A reference, list or choice field (some share a name with their ORDER_PARAM entry, so test this first). */
function isRefKey(k: string): k is RefKey {
  return REF_KEYS.includes(k)
}

/** True when numeric param `k` is one the worker accepts on `order` (in `limits`, and the order's role has it). */
export function paramApplies(orders: OrdersConfig, order: OrderName, k: OrderParamName): boolean {
  if (orders.limits[k] === undefined) return false
  const role: Partial<Record<OrderParamName, string>> = ROLE_PARAM_FIELDS[orders.orders[order].role]
  return !isRoleParam(k) || role[k] !== undefined
}

/** Parses orders.json, roles.json and orderEditor.json; throws when an editor field is not one the worker accepts. */
export function loadOrderEditorData(ordersRaw: unknown = ordersJson, rolesRaw: unknown = rolesJson, cfgRaw: unknown = editorJson): OrderEditorData {
  const orders = ordersConfigSchema.parse(ordersRaw)
  const roles = rolesShape.parse(rolesRaw)
  const cfg = editorConfigSchema.parse(cfgRaw)
  const roleDefaults = {} as Record<OrderRole, Partial<Record<OrderParamName, number>>>
  for (const role of Object.keys(ROLE_PARAM_FIELDS) as OrderRole[]) {
    const out: Partial<Record<OrderParamName, number>> = {}
    for (const [p, field] of Object.entries(ROLE_PARAM_FIELDS[role]) as [OrderParamName, string][]) {
      const v = roles[role][field]
      if (typeof v === 'number') out[p] = v
    }
    roleDefaults[role] = out
  }
  const fields = {} as Record<OrderName, FieldKey[]>
  for (const order of ORDER_NAMES) {
    fields[order] = cfg.fields[order].map((k) => {
      if (isRefKey(k)) return k
      if (!isParam(k) || k === 'stopDay' || !paramApplies(orders, order, k)) throw new Error(`orderEditor.json fields.${order}: "${k}" is not a param the worker accepts on a ${order} order`)
      return k
    })
  }
  return { orders, roleDefaults, config: { ...cfg, fields } }
}

// ---------------------------------------------------------------------------------------------
// Options, defaults, presets
// ---------------------------------------------------------------------------------------------

export interface Choice {
  readonly id: string
  readonly name: string
}

/** The choices the host knows for the ship being edited. */
export interface EditorOptions {
  readonly shipId: string
  readonly shipClass: ShipClassName
  /** Trade wares (allowed / blocked / supply priority) and mined ores. */
  readonly wares: readonly Choice[]
  readonly ores: readonly Choice[]
  readonly systems: readonly Choice[]
  /** Ferry destinations (any station) and the player's own stations (supply target, `myStation` sink). */
  readonly stations: readonly Choice[]
  readonly playerStations: readonly Choice[]
  readonly relays: readonly Choice[]
  /** Ships to follow: the player (`player`) and the other fleet ships. */
  readonly escorts: readonly Choice[]
}

/** The orders ship class `cls` may take (orders.json `classes`). */
export function ordersFor(data: OrderEditorData, cls: ShipClassName): OrderName[] {
  return ORDER_NAMES.filter((o) => data.orders.orders[o].classes.includes(cls))
}

/** The presets ship class `cls` may take. */
export function presetsFor(data: OrderEditorData, cls: ShipClassName): OrdersConfig['presets'] {
  return data.orders.presets.filter((p) => data.orders.orders[p.order].classes.includes(cls))
}

/** The value a param runs at when the order does not set it (orders.json `params`, else roles.json, else 0). */
export function paramDefault(data: OrderEditorData, order: OrderName, k: OrderParamName): number {
  const def = data.orders.orders[order]
  return def.params[k] ?? data.roleDefaults[def.role][k] ?? 0
}

function defaultSellPolicy(data: OrderEditorData, order: OrderName): SellPolicyName {
  return data.orders.orders[order].sellPolicy ?? 'best'
}

function defaultPatrolRule(data: OrderEditorData, order: OrderName): PatrolRuleName {
  return data.orders.orders[order].patrolRule ?? 'defendAssets'
}

/** The param's value in `o`: its override, else the default. */
export function paramValue(data: OrderEditorData, o: StandingOrder, k: OrderParamName): number {
  if (k === 'stopDay') return o.stopDay ?? 0
  return o.params[k] ?? paramDefault(data, o.order, k)
}

/** Float32 values from the worker's view back to the decimals a player sets (0.11999999731779099 → 0.12). */
function tidy(v: number): number {
  return Number(v.toPrecision(6))
}

/** A deep copy of a view's order, float32 noise removed. */
export function draftFromView(o: StandingOrder): StandingOrder {
  const params: Partial<Record<OrderParamName, number>> = {}
  for (const [k, v] of Object.entries(o.params) as [OrderParamName, number][]) params[k] = tidy(v)
  const out: StandingOrder = { ...o, stops: [...o.stops], params, wares: [...o.wares], blockWares: [...o.blockWares], systems: [...o.systems] }
  if (o.priority !== undefined) out.priority = [...o.priority]
  if (o.stopDay !== undefined) out.stopDay = tidy(o.stopDay)
  return out
}

/** A fresh order `order` keeping the stop conditions: the order's default ROE, no overrides, no references. */
export function changeOrder(data: OrderEditorData, o: StandingOrder, order: OrderName): StandingOrder {
  const out: StandingOrder = { order, roe: data.orders.orders[order].roe, stops: [...o.stops], params: {}, wares: [], blockWares: [], systems: [] }
  if (o.stopDay !== undefined) out.stopDay = o.stopDay
  return out
}

/**
 * Preset `id` applied to `o` as the worker's `applyPreset` does (presets.ts): the preset's order, ROE, stops,
 * params, sell policy, patrol rule and wares; the ship's references (sink, target, priority, relay, escort) and
 * explicit area kept; a `myStation` policy without a sink falls back to `best`.
 */
export function presetOrder(data: OrderEditorData, o: StandingOrder, id: string): StandingOrder | null {
  const p = data.orders.presets.find((x) => x.id === id)
  if (p === undefined) return null
  const out: StandingOrder = {
    order: p.order,
    roe: p.roe,
    stops: [...p.stops],
    params: { ...p.params },
    wares: [...(p.wares ?? [])],
    blockWares: [...p.blockWares],
    systems: [...o.systems],
  }
  if (p.sellPolicy !== undefined) out.sellPolicy = p.sellPolicy
  if (p.patrolRule !== undefined) out.patrolRule = p.patrolRule
  if (o.sellStation !== undefined) out.sellStation = o.sellStation
  if (o.target !== undefined) out.target = o.target
  if (o.priority !== undefined) out.priority = [...o.priority]
  if (o.laneRelay !== undefined) out.laneRelay = o.laneRelay
  if (o.escort !== undefined) out.escort = o.escort
  if (out.sellPolicy === 'myStation' && out.sellStation === undefined) out.sellPolicy = 'best'
  return out
}

// ---------------------------------------------------------------------------------------------
// The command and validation
// ---------------------------------------------------------------------------------------------

function hasField(data: OrderEditorData, order: OrderName, k: FieldKey): boolean {
  return data.config.fields[order].includes(k)
}

const wareOrder = (a: WareId, b: WareId): number => WARE_IDS.indexOf(a) - WARE_IDS.indexOf(b)

/**
 * The order as the `setOrder` command carries it: the order's own fields only, params that differ from the
 * defaults, the sell policy and patrol rule always (as the worker's view lists them), stops and wares in the
 * worker's order, systems sorted, the stop day only with the date stop.
 */
export function commandOrder(data: OrderEditorData, o: StandingOrder): StandingOrder {
  const order = o.order
  const params: Partial<Record<OrderParamName, number>> = {}
  for (const k of data.config.fields[order]) {
    if (isRefKey(k)) continue
    const v = o.params[k]
    if (v !== undefined && v !== paramDefault(data, order, k)) params[k] = v
  }
  const out: StandingOrder = {
    order,
    roe: o.roe,
    stops: STOP_NAMES.filter((s) => o.stops.includes(s)),
    params,
    wares: hasField(data, order, 'wares') ? [...o.wares].sort(wareOrder) : [],
    blockWares: hasField(data, order, 'blockWares') ? [...o.blockWares].sort(wareOrder) : [],
    systems: hasField(data, order, 'systems') ? [...o.systems].sort() : [],
    sellPolicy: o.sellPolicy ?? defaultSellPolicy(data, order),
    patrolRule: o.patrolRule ?? defaultPatrolRule(data, order),
  }
  if (hasField(data, order, 'sellStation') && out.sellPolicy === 'myStation' && o.sellStation !== undefined) out.sellStation = o.sellStation
  if (hasField(data, order, 'target') && o.target !== undefined) out.target = o.target
  if (hasField(data, order, 'priority') && o.priority !== undefined && o.priority.length > 0) out.priority = [...o.priority]
  if (hasField(data, order, 'laneRelay') && out.patrolRule === 'guardLane' && o.laneRelay !== undefined) out.laneRelay = o.laneRelay
  if (hasField(data, order, 'escort') && o.escort !== undefined) out.escort = o.escort
  if (out.stops.includes('date') && o.stopDay !== undefined) out.stopDay = o.stopDay
  return out
}

export interface Issue {
  readonly field: FieldKey | 'order' | 'stopDay' | 'stops' | 'roe'
  readonly message: string
}

function limitIssue(data: OrderEditorData, k: OrderParamName, v: number): string | null {
  if (!Number.isFinite(v)) return 'must be a number'
  const lim = data.orders.limits[k]
  if (lim === undefined) return null
  return v < lim[0] || v > lim[1] ? `must be between ${lim[0]} and ${lim[1]}` : null
}

/** Everything the worker would refuse (and out-of-limits values it would clamp) in `o` for this ship. Empty = valid. */
export function validateOrder(data: OrderEditorData, o: StandingOrder, opts: Pick<EditorOptions, 'shipId' | 'shipClass'>): Issue[] {
  const issues: Issue[] = []
  if (!data.orders.orders[o.order].classes.includes(opts.shipClass)) issues.push({ field: 'order', message: `A ${opts.shipClass} cannot take a ${o.order} order` })
  for (const [k, v] of Object.entries(o.params) as [OrderParamName, number][]) {
    if (!paramApplies(data.orders, o.order, k) || k === 'stopDay') {
      issues.push({ field: k, message: `${k} does not apply to a ${o.order} order` })
      continue
    }
    const bad = limitIssue(data, k, v)
    if (bad !== null) issues.push({ field: k, message: `${data.config.labels.params[k] ?? k} ${bad}` })
  }
  if (o.stops.includes('date')) {
    if (o.stopDay === undefined) issues.push({ field: 'stopDay', message: 'The date stop needs a day' })
    else {
      const bad = limitIssue(data, 'stopDay', o.stopDay)
      if (bad !== null || !Number.isInteger(o.stopDay)) issues.push({ field: 'stopDay', message: `${data.config.labels.params.stopDay ?? 'stopDay'} ${bad ?? 'must be a whole day'}` })
    }
  }
  const cmd = commandOrder(data, o)
  if (hasField(data, o.order, 'sellPolicy') && cmd.sellPolicy === 'myStation' && cmd.sellStation === undefined) issues.push({ field: 'sellStation', message: 'Deliver to my station needs a station' })
  if ((o.order === 'supply' || o.order === 'ferry') && cmd.target === undefined) issues.push({ field: 'target', message: `A ${o.order} order needs a target station` })
  if (o.order === 'patrol' && cmd.patrolRule === 'guardLane' && cmd.laneRelay === undefined) issues.push({ field: 'laneRelay', message: 'Guard a lane needs a relay' })
  if (o.order === 'escort') {
    if (cmd.escort === undefined) issues.push({ field: 'escort', message: 'An escort order needs a ship to follow' })
    else if (cmd.escort === opts.shipId) issues.push({ field: 'escort', message: 'A ship cannot escort itself' })
  }
  if (issues.length === 0) {
    const parsed = standingOrderSchema.safeParse(cmd)
    if (!parsed.success) for (const e of parsed.error.issues) issues.push({ field: 'order', message: `${e.path.join('.')}: ${e.message}` })
  }
  return issues
}

/** The command that saves the editor: `applyPreset` when the draft is an unchanged preset, else `setOrder`. */
export function saveCommand(data: OrderEditorData, shipId: string, o: StandingOrder, preset: string | null): FleetCommand {
  if (preset !== null) return { type: 'applyPreset', shipIds: [shipId], preset }
  return { type: 'setOrder', shipId, order: commandOrder(data, o) }
}

// ---------------------------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------------------------

export type EditorRow =
  | { readonly kind: 'preset' }
  | { readonly kind: 'order' }
  | { readonly kind: 'roe' }
  | { readonly kind: 'stop'; readonly stop: StopName }
  | { readonly kind: 'stopDay' }
  | { readonly kind: 'number'; readonly param: OrderParamName }
  | { readonly kind: 'enum'; readonly field: EnumField }
  | { readonly kind: 'multi'; readonly field: MultiField }
  | { readonly kind: 'ref'; readonly field: RefField }
  | { readonly kind: 'action'; readonly action: 'save' | 'revert' | 'close' }

/** The editor's rows for order `o`: preset, order, ROE, the order's fields, stops, then save / revert / close. */
export function editorRows(data: OrderEditorData, o: StandingOrder): EditorRow[] {
  const rows: EditorRow[] = [{ kind: 'preset' }, { kind: 'order' }, { kind: 'roe' }]
  const sell = o.sellPolicy ?? defaultSellPolicy(data, o.order)
  const rule = o.patrolRule ?? defaultPatrolRule(data, o.order)
  for (const k of data.config.fields[o.order]) {
    if (!isRefKey(k)) rows.push({ kind: 'number', param: k })
    else if (k === 'sellPolicy' || k === 'patrolRule') rows.push({ kind: 'enum', field: k })
    else if (k === 'sellStation') {
      if (sell === 'myStation') rows.push({ kind: 'ref', field: k })
    } else if (k === 'laneRelay') {
      if (rule === 'guardLane') rows.push({ kind: 'ref', field: k })
    } else if (k === 'target' || k === 'escort') rows.push({ kind: 'ref', field: k })
    else rows.push({ kind: 'multi', field: k })
  }
  for (const s of STOP_NAMES) {
    rows.push({ kind: 'stop', stop: s })
    if (s === 'date' && o.stops.includes('date')) rows.push({ kind: 'stopDay' })
  }
  rows.push({ kind: 'action', action: 'save' }, { kind: 'action', action: 'revert' }, { kind: 'action', action: 'close' })
  return rows
}

/** The choices of a list or reference field on order `order`. */
export function choicesOf(opts: EditorOptions, order: OrderName, field: MultiField | RefField): readonly Choice[] {
  switch (field) {
    case 'wares':
      return order === 'mine' ? opts.ores : opts.wares
    case 'blockWares':
    case 'priority':
      return opts.wares
    case 'systems':
      return opts.systems
    case 'sellStation':
      return opts.playerStations
    case 'target':
      return order === 'supply' ? opts.playerStations : opts.stations
    case 'laneRelay':
      return opts.relays
    case 'escort':
      return opts.escorts.filter((c) => c.id !== opts.shipId)
  }
}

/** The ids a list field holds. */
export function listOf(o: StandingOrder, field: MultiField): readonly string[] {
  return field === 'priority' ? (o.priority ?? []) : o[field]
}

// ---------------------------------------------------------------------------------------------
// State and reducer
// ---------------------------------------------------------------------------------------------

export interface EditorState {
  /** The ship's order as the worker reported it (revert goes back to it). */
  readonly base: StandingOrder
  readonly basePreset: string | null
  readonly draft: StandingOrder
  /** The preset the draft is, unchanged (saved as `applyPreset`); null once edited. */
  readonly preset: string | null
  /** The preset row's choice (index into the class's presets). */
  readonly presetPick: number
  readonly cursor: number
  /** The option cursor on a list row. */
  readonly pick: number
  readonly saving: boolean
  readonly notice: string | null
}

export function createEditorState(order: StandingOrder, preset: string | null): EditorState {
  const draft = draftFromView(order)
  return { base: draft, basePreset: preset, draft, preset: null, presetPick: 0, cursor: 0, pick: 0, saving: false, notice: null }
}

export type EditorAction =
  | { type: 'nav'; input: NavInput }
  | { type: 'cursor'; index: number }
  | { type: 'step'; dir: -1 | 1 }
  | { type: 'setNumber'; param: OrderParamName; value: number }
  | { type: 'toggleOption'; field: MultiField; id: string }
  | { type: 'setRef'; field: RefField; id: string | null }
  | { type: 'setEnum'; field: EnumField; value: string }
  | { type: 'setOrder'; order: OrderName }
  | { type: 'setRoe'; roe: RoeName }
  | { type: 'toggleStop'; stop: StopName }
  | { type: 'pickPreset'; index: number }
  | { type: 'applyPreset' }
  | { type: 'save' }
  | { type: 'saved'; ok: boolean; error?: string }
  | { type: 'revert' }
  | { type: 'close' }
  /** The worker's view of the ship came back after a save or a refresh. */
  | { type: 'reset'; order: StandingOrder; preset: string | null; notice?: string }

export type EditorEffect = { kind: 'save'; cmd: FleetCommand } | { kind: 'close' }

export interface EditorResult {
  state: EditorState
  effect: EditorEffect | null
}

export interface EditorCtx {
  readonly data: OrderEditorData
  readonly opts: EditorOptions
}

function cycle<T>(list: readonly T[], cur: T, dir: number): T {
  if (list.length === 0) return cur
  const i = list.indexOf(cur)
  return list[(((i < 0 ? (dir > 0 ? -1 : 0) : i) + dir) % list.length + list.length) % list.length]
}

function round(v: number, step: number): number {
  const decimals = Math.max(0, Math.ceil(-Math.log10(step)) + 1)
  return Number(v.toFixed(decimals))
}

/** A user edit: the draft is now custom (no preset). */
function edited(s: EditorState, draft: StandingOrder): EditorState {
  return { ...s, draft, preset: null, notice: null }
}

function stepNumber(ctx: EditorCtx, s: EditorState, param: OrderParamName, dir: number): EditorState {
  const step = ctx.data.config.steps[param] ?? 1
  const lim = ctx.data.orders.limits[param]
  const cur = paramValue(ctx.data, s.draft, param)
  let v = round(cur + dir * step, step)
  if (lim !== undefined) v = Math.min(lim[1], Math.max(lim[0], v))
  if (param === 'stopDay') return edited(s, { ...s.draft, stopDay: v })
  return edited(s, { ...s.draft, params: { ...s.draft.params, [param]: v } })
}

function toggleList(o: StandingOrder, field: MultiField, id: string): StandingOrder {
  const cur = listOf(o, field)
  if (field === 'priority') {
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : cur.length >= 4 ? cur : [...cur, id]
    return { ...o, priority: next as WareId[] }
  }
  const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]
  return field === 'systems' ? { ...o, systems: next } : { ...o, [field]: next as WareId[] }
}

function setRef(o: StandingOrder, field: RefField, id: string | null): StandingOrder {
  if (id !== null) return { ...o, [field]: id }
  const { [field]: _dropped, ...rest } = o
  void _dropped
  return rest
}

function refCycle(ctx: EditorCtx, s: EditorState, field: RefField, dir: number): EditorState {
  const ids: (string | null)[] = [null, ...choicesOf(ctx.opts, s.draft.order, field).map((c) => c.id)]
  const next = cycle(ids, s.draft[field] ?? null, dir)
  return edited(s, setRef(s.draft, field, next))
}

function withStopDay(ctx: EditorCtx, o: StandingOrder, stop: StopName): StandingOrder {
  const on = o.stops.includes(stop)
  const stops = on ? o.stops.filter((x) => x !== stop) : [...o.stops, stop]
  const out: StandingOrder = { ...o, stops }
  if (stop === 'date' && !on && out.stopDay === undefined) out.stopDay = ctx.data.orders.limits.stopDay?.[0] ?? 0
  return out
}

function horizontal(ctx: EditorCtx, s: EditorState, row: EditorRow, dir: -1 | 1): EditorState {
  switch (row.kind) {
    case 'preset': {
      const n = presetsFor(ctx.data, ctx.opts.shipClass).length
      return n === 0 ? s : { ...s, presetPick: (((s.presetPick + dir) % n) + n) % n }
    }
    case 'order': {
      const next = cycle(ordersFor(ctx.data, ctx.opts.shipClass), s.draft.order, dir)
      return next === s.draft.order ? s : edited(s, changeOrder(ctx.data, s.draft, next))
    }
    case 'roe':
      return edited(s, { ...s.draft, roe: cycle(ROE_NAMES, s.draft.roe, dir) })
    case 'number':
      return stepNumber(ctx, s, row.param, dir)
    case 'stopDay':
      return stepNumber(ctx, s, 'stopDay', dir)
    case 'enum':
      return row.field === 'sellPolicy'
        ? edited(s, { ...s.draft, sellPolicy: cycle(SELL_POLICY_NAMES, s.draft.sellPolicy ?? defaultSellPolicy(ctx.data, s.draft.order), dir) })
        : edited(s, { ...s.draft, patrolRule: cycle(PATROL_RULE_NAMES, s.draft.patrolRule ?? defaultPatrolRule(ctx.data, s.draft.order), dir) })
    case 'ref':
      return refCycle(ctx, s, row.field, dir)
    case 'multi': {
      const n = choicesOf(ctx.opts, s.draft.order, row.field).length
      return n === 0 ? s : { ...s, pick: (((s.pick + dir) % n) + n) % n }
    }
    case 'stop':
    case 'action':
      return s
  }
}

function activate(ctx: EditorCtx, s: EditorState, row: EditorRow): EditorResult {
  switch (row.kind) {
    case 'preset':
      return reduceEditor(s, { type: 'applyPreset' }, ctx)
    case 'stop':
      return { state: edited(s, withStopDay(ctx, s.draft, row.stop)), effect: null }
    case 'multi': {
      const c = choicesOf(ctx.opts, s.draft.order, row.field)[s.pick]
      return { state: c === undefined ? s : edited(s, toggleList(s.draft, row.field, c.id)), effect: null }
    }
    case 'action':
      return reduceEditor(s, { type: row.action }, ctx)
    default:
      return { state: horizontal(ctx, s, row, 1), effect: null }
  }
}

/** The pure editor reducer. */
export function reduceEditor(s: EditorState, a: EditorAction, ctx: EditorCtx): EditorResult {
  const rows = editorRows(ctx.data, s.draft)
  const row = rows[Math.min(s.cursor, rows.length - 1)]
  const done = (state: EditorState): EditorResult => {
    const n = editorRows(ctx.data, state.draft).length
    return { state: state.cursor >= n ? { ...state, cursor: n - 1 } : state, effect: null }
  }
  switch (a.type) {
    case 'nav':
      switch (a.input) {
        case 'up':
          return done({ ...s, cursor: Math.max(0, s.cursor - 1), pick: 0 })
        case 'down':
          return done({ ...s, cursor: Math.min(rows.length - 1, s.cursor + 1), pick: 0 })
        case 'left':
          return done(horizontal(ctx, s, row, -1))
        case 'right':
          return done(horizontal(ctx, s, row, 1))
        case 'activate': {
          const r = activate(ctx, s, row)
          return r.effect === null ? done(r.state) : r
        }
        case 'back':
          return { state: s, effect: { kind: 'close' } }
        case 'start':
          return { state: s, effect: null }
      }
      return { state: s, effect: null }
    case 'cursor':
      return done({ ...s, cursor: Math.max(0, Math.min(rows.length - 1, a.index)), pick: 0 })
    case 'step':
      return done(horizontal(ctx, s, row, a.dir))
    case 'setNumber':
      if (a.param === 'stopDay') return done(edited(s, { ...s.draft, stopDay: a.value }))
      return done(edited(s, { ...s.draft, params: { ...s.draft.params, [a.param]: a.value } }))
    case 'toggleOption':
      return done(edited(s, toggleList(s.draft, a.field, a.id)))
    case 'setRef':
      return done(edited(s, setRef(s.draft, a.field, a.id)))
    case 'setEnum':
      if (a.field === 'sellPolicy' && (SELL_POLICY_NAMES as readonly string[]).includes(a.value)) return done(edited(s, { ...s.draft, sellPolicy: a.value as SellPolicyName }))
      if (a.field === 'patrolRule' && (PATROL_RULE_NAMES as readonly string[]).includes(a.value)) return done(edited(s, { ...s.draft, patrolRule: a.value as PatrolRuleName }))
      return { state: s, effect: null }
    case 'setOrder':
      if (a.order === s.draft.order || !ordersFor(ctx.data, ctx.opts.shipClass).includes(a.order)) return { state: s, effect: null }
      return done(edited(s, changeOrder(ctx.data, s.draft, a.order)))
    case 'setRoe':
      return done(edited(s, { ...s.draft, roe: a.roe }))
    case 'toggleStop':
      return done(edited(s, withStopDay(ctx, s.draft, a.stop)))
    case 'pickPreset':
      return { state: { ...s, presetPick: a.index }, effect: null }
    case 'applyPreset': {
      const p = presetsFor(ctx.data, ctx.opts.shipClass)[s.presetPick]
      const draft = p === undefined ? null : presetOrder(ctx.data, s.draft, p.id)
      if (p === undefined || draft === null) return { state: { ...s, notice: 'No preset fits this ship' }, effect: null }
      return done({ ...s, draft, preset: p.id, notice: `${p.name} filled in; save to give the order` })
    }
    case 'save': {
      if (s.saving) return { state: s, effect: null }
      const issues = validateOrder(ctx.data, s.draft, ctx.opts)
      if (issues.length > 0) return { state: { ...s, notice: issues[0].message }, effect: null }
      return { state: { ...s, saving: true, notice: 'Saving…' }, effect: { kind: 'save', cmd: saveCommand(ctx.data, ctx.opts.shipId, s.draft, s.preset) } }
    }
    case 'saved':
      return { state: { ...s, saving: false, notice: a.ok ? 'Order saved' : `The order was refused: ${a.error ?? 'unknown'}` }, effect: null }
    case 'revert':
      return done({ ...s, draft: s.base, preset: null, notice: 'Reverted' })
    case 'close':
      return { state: s, effect: { kind: 'close' } }
    case 'reset': {
      const next = createEditorState(a.order, a.preset)
      return done({ ...next, cursor: s.cursor, presetPick: s.presetPick, notice: a.notice ?? s.notice })
    }
  }
}

/** True when the draft differs from the ship's order. */
export function isDirty(data: OrderEditorData, s: EditorState): boolean {
  return s.preset !== null || JSON.stringify(commandOrder(data, s.draft)) !== JSON.stringify(commandOrder(data, s.base))
}

/** The display text of a numeric value. */
export function numberText(k: OrderParamName, v: number): string {
  if (k === 'capital' || k === 'budget') return `${Math.round(v).toLocaleString('en-US')} cr`
  if (k === 'minMargin' || k === 'restockMargin' || k === 'minDeficit') return `${round(v * 100, 0.1)}%`
  return `${v}`
}
